import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, useNavigate, useMatch, Link, NavLink } from 'react-router-dom';
import { api } from './api';
import { useAuth } from './context/AuthContext';
import { useTheme, THEMES, BRAND_THEME, BRAND_MODE } from './context/ThemeContext';
import { useCompanion } from './lib/useCompanion';
// Eager-loaded: the first page every cold visit can land on. Splitting these
// out would just add a Suspense flash without shrinking the critical chunk.
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { VerifyEmailBanner } from './components/VerifyEmailBanner';
import { CommandPalette } from './components/CommandPalette';
import { ShortcutsModal } from './components/ShortcutsModal';
import { CookieBanner } from './components/CookieBanner';
import { capturePageview } from './lib/analytics';

// Lazy-loaded: everything else. The editor, sequencer, and project pages
// alone pull in wavesurfer, soundtouch-ts, and the DSP wasm — the landing
// page should never pay that cost. zxcvbn (password strength) is behind
// Register/Reset so it stops bloating the first-paint critical path too.
const RegisterPage = lazy(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage').then(m => ({ default: m.ChangelogPage })));
const LegalPage = lazy(() => import('./pages/LegalPage').then(m => ({ default: m.LegalPage })));
const CreditsPage = lazy(() => import('./pages/CreditsPage').then(m => ({ default: m.CreditsPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const LibraryPage = lazy(() => import('./pages/LibraryPage').then(m => ({ default: m.LibraryPage })));
const AccountPage = lazy(() => import('./pages/AccountPage').then(m => ({ default: m.AccountPage })));
const ConfirmEmailChangePage = lazy(() => import('./pages/ConfirmEmailChangePage').then(m => ({ default: m.ConfirmEmailChangePage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const ProjectPage = lazy(() => import('./pages/ProjectPage').then(m => ({ default: m.ProjectPage })));
const EditorPage = lazy(() => import('./pages/EditorPage').then(m => ({ default: m.EditorPage })));
const InvitesPage = lazy(() => import('./pages/InvitesPage').then(m => ({ default: m.InvitesPage })));
const FriendsPage = lazy(() => import('./pages/FriendsPage').then(m => ({ default: m.FriendsPage })));
const SequencerPage = lazy(() => import('./pages/SequencerPage').then(m => ({ default: m.SequencerPage })));

const RouteFallback = () => <div className="loading"><div className="spinner" /></div>;

function StatusBar() {
  const companion = useCompanion();
  return (
    <div className="status-bar">
      <span className="status-bar-section">
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/dmca">DMCA</Link>
        <Link to="/credits">Credits</Link>
        <Link to="/changelog">Changelog</Link>
      </span>
      <span className="status-bar-section status-bar-right">
        {companion.available ? (
          <span className="link-status link-connected">
            <span className="link-dot" />
            Link connected ({companion.fileCount} files)
          </span>
        ) : (
          <span className="link-status link-download">
            Get SmpleCut Link:
            <a href="/download/SmpleCut-Link.dmg">macOS</a>
            <a href="/download/SmpleCut-Link-Setup.exe">Windows</a>
          </span>
        )}
      </span>
    </div>
  );
}

function ThemePicker() {
  const { theme, mode, setTheme, toggleMode } = useTheme();
  return (
    <div className="theme-picker">
      {THEMES.map(t => (
        <button
          key={t.id}
          className={`theme-swatch ${theme === t.id ? 'active' : ''}`}
          style={{ background: t.swatch }}
          onClick={() => setTheme(t.id)}
          title={t.label}
        />
      ))}
      <button
        className="btn btn-sm mode-toggle"
        onClick={toggleMode}
        title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
      >
        {mode === 'dark' ? 'LT' : 'DK'}
      </button>
    </div>
  );
}

export function App() {
  const { user, loading } = useAuth();
  const { theme, mode } = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const location = useLocation();

  // Logged-out visitors always see the brand theme regardless of any theme
  // they picked in a previous session. Once signed in, their own preference
  // wins again — ThemeProvider's effect covers that side of the toggle.
  useEffect(() => {
    if (!user) {
      document.documentElement.setAttribute('data-theme', BRAND_THEME);
      document.documentElement.setAttribute('data-mode', BRAND_MODE);
    } else {
      document.documentElement.setAttribute('data-theme', theme);
      document.documentElement.setAttribute('data-mode', mode);
    }
  }, [user, theme, mode]);

  useEffect(() => {
    capturePageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Cmd+K / Ctrl+K opens the command palette from anywhere in the signed-in
  // app. '?' opens the shortcuts modal — kept consistent with the in-editor
  // binding documented in ShortcutsModal.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      } else if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const target = e.target as HTMLElement | null;
        // Don't hijack '?' when the user is typing into a form.
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
        e.preventDefault();
        setShortcutsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (loading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  if (!user) {
    return (
      <>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/privacy" element={<LegalPage slug="privacy" />} />
            <Route path="/terms" element={<LegalPage slug="terms" />} />
            <Route path="/dmca" element={<LegalPage slug="dmca" />} />
            <Route path="/credits" element={<CreditsPage />} />
            <Route path="/account/confirm-email-change" element={<ConfirmEmailChangePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        <CookieBanner />
      </>
    );
  }

  return (
    <div className="app">
      <NavBar onSearchOpen={() => setPaletteOpen(true)} userName={user.name} />
      <VerifyEmailBanner />
      <main className="main-content">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:id" element={<ProjectPage />} />
            <Route path="/projects/:projectId/tracks/:trackId" element={<EditorPage />} />
            <Route path="/projects/:projectId/sequencer" element={<SequencerPage />} />
            <Route path="/invites" element={<InvitesPage />} />
            <Route path="/friends" element={<FriendsPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/privacy" element={<LegalPage slug="privacy" />} />
            <Route path="/terms" element={<LegalPage slug="terms" />} />
            <Route path="/dmca" element={<LegalPage slug="dmca" />} />
            <Route path="/credits" element={<CreditsPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/account/confirm-email-change" element={<ConfirmEmailChangePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <StatusBar />
      {paletteOpen && (
        <PaletteWrapper
          onClose={() => setPaletteOpen(false)}
          onOpenShortcuts={() => setShortcutsOpen(true)}
        />
      )}
      {shortcutsOpen && <ShortcutsModal onClose={() => setShortcutsOpen(false)} />}
      <CookieBanner />
    </div>
  );
}

function Breadcrumbs() {
  const projectsMatch = useMatch('/projects');
  const projectMatch = useMatch('/projects/:id');
  const trackMatch = useMatch('/projects/:projectId/tracks/:trackId');
  const sequencerMatch = useMatch('/projects/:projectId/sequencer');
  const invitesMatch = useMatch('/invites');
  const friendsMatch = useMatch('/friends');

  const projectId =
    projectMatch?.params.id ??
    trackMatch?.params.projectId ??
    sequencerMatch?.params.projectId ??
    null;
  const trackId = trackMatch?.params.trackId ?? null;
  const inProjectsTree = !!(projectsMatch || projectId);

  const [projectTitle, setProjectTitle] = useState<string | null>(null);
  const [trackTitle, setTrackTitle] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) { setProjectTitle(null); return; }
    let cancelled = false;
    api.getProject(projectId)
      .then((p) => { if (!cancelled) setProjectTitle(p.title); })
      .catch(() => { if (!cancelled) setProjectTitle(null); });
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !trackId) { setTrackTitle(null); return; }
    let cancelled = false;
    api.getTrack(projectId, trackId)
      .then((t) => { if (!cancelled) setTrackTitle(t.title); })
      .catch(() => { if (!cancelled) setTrackTitle(null); });
    return () => { cancelled = true; };
  }, [projectId, trackId]);

  return (
    <div className="breadcrumbs">
      <Link to="/">Home</Link>
      {invitesMatch && (
        <>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Invites</span>
        </>
      )}
      {friendsMatch && (
        <>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Friends</span>
        </>
      )}
      {inProjectsTree && (
        <>
          <span className="breadcrumb-sep">/</span>
          {projectId ? (
            <Link to="/projects">Projects</Link>
          ) : (
            <span className="breadcrumb-current">Projects</span>
          )}
        </>
      )}
      {projectId && (
        <>
          <span className="breadcrumb-sep">/</span>
          {trackId || sequencerMatch ? (
            <Link to={`/projects/${projectId}`}>{projectTitle ?? '…'}</Link>
          ) : (
            <span className="breadcrumb-current">{projectTitle ?? '…'}</span>
          )}
        </>
      )}
      {trackId && (
        <>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">{trackTitle ?? '…'}</span>
        </>
      )}
      {sequencerMatch && (
        <>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">Sequencer</span>
        </>
      )}
    </div>
  );
}

function NavBar({ onSearchOpen: onPaletteOpen, userName }: { onSearchOpen: () => void; userName: string }) {
  const { logout } = useAuth();
  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };
  const navLinkClass = ({ isActive }: { isActive: boolean }) => isActive ? 'active' : '';
  return (
    <nav className="navbar">
      <Link to="/" className="nav-brand">SmpleCut</Link>
      <Breadcrumbs />
      <div className="nav-links">
        <NavLink to="/projects" className={navLinkClass}>Projects</NavLink>
        <NavLink to="/library" className={navLinkClass}>Library</NavLink>
        <NavLink to="/friends" className={navLinkClass}>Friends</NavLink>
        <NavLink to="/invites" className={navLinkClass}>Invites</NavLink>
        <span className="nav-sep">|</span>
        <button
          onClick={onPaletteOpen}
          className="btn btn-sm nav-palette-btn"
          title="Open command palette (Cmd/Ctrl+K)"
          aria-label="Open command palette"
        >
          ⌘K
        </button>
        <ThemePicker />
        <span className="nav-sep">|</span>
        <NavLink to="/account" className="nav-user" title="Account settings">{userName}</NavLink>
        <button onClick={handleLogout} className="btn btn-sm">
          Logout
        </button>
      </div>
    </nav>
  );
}

function PaletteWrapper({ onClose, onOpenShortcuts }: { onClose: () => void; onOpenShortcuts: () => void }) {
  const navigate = useNavigate();
  return (
    <CommandPalette
      onClose={onClose}
      onNavigate={(href) => navigate(href)}
      onOpenShortcuts={onOpenShortcuts}
    />
  );
}
